'use client';
import { useRef, useState } from 'react';
import { useToast } from '@practium/ui';
import type { TestimonialFormData } from '../types/landing.types';
import { createTestimonial, updateTestimonial } from '../services/testimonial.service';
import { TestimonialCreateSchema, TestimonialUpdateSchema, TESTIMONIAL_FIELD_LABELS } from '../schemas/landing.schema';
import { DEFAULT_TESTIMONIAL_FORM } from '../constants/landing.constants';

export interface UseTestimonialFormOptions {
	initialData?: TestimonialFormData;
	testimonialId?: string;
	avatarFile?: File | null;
	onSubmit: () => void;
}

export const useTestimonialForm = ({
	initialData,
	testimonialId,
	avatarFile,
	onSubmit,
}: UseTestimonialFormOptions) => {
	const toast = useToast();
	const isEdit = !!testimonialId;

	const initialForm = initialData
		? { ...DEFAULT_TESTIMONIAL_FORM, ...initialData }
		: { ...DEFAULT_TESTIMONIAL_FORM };
	const [form, setForm] = useState<TestimonialFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const set = <K extends keyof TestimonialFormData>(key: K, value: TestimonialFormData[K]) =>
		setForm(f => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const schema = isEdit ? TestimonialUpdateSchema : TestimonialCreateSchema;
		const result = schema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach(issue => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [...new Set(result.error.issues.map(i => String(i.path[0] ?? '')).filter(Boolean))];
			const labels = uniquePaths.map(p => TESTIMONIAL_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);

		try {
			const formData = new FormData();
			formData.append('name', form.name);
			formData.append('occupation', form.occupation);
			formData.append('testimonial', form.testimonial ?? '');
			formData.append('status', form.status);
			if (avatarFile) formData.append('avatar', avatarFile);

			if (isEdit) {
				await updateTestimonial(testimonialId, formData);
				toast.success('Depoimento atualizado com sucesso');
			} else {
				await createTestimonial(formData);
				toast.success('Depoimento criado com sucesso');
			}
			onSubmit();
		} catch {
			toast.error('Erro ao salvar depoimento');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, errors, isLoading, set, handleSubmit, isDirty };
};
